/**
 * The 11 PM IST review deadline, rendered as time remaining.
 *
 * At that moment the backend cutoff job (`schedule_day_ahead_cutoff`) ships
 * every outfit still awaiting review exactly as generated. The countdown keeps
 * working past zero; it does not lock the surface itself.
 */

import { useEffect, useState } from 'react';
import { StatTile } from '@ui/console/primitives';
import { formatCountdown, millisecondsToCutoff } from './review-cutoff';

/** `now` freezes the clock, for tests and for a row rendered from queue load. */
export function useTimeToCutoff(targetDate: string, now?: Date): number {
  const frozen = now?.getTime();
  const [remaining, setRemaining] = useState(() =>
    millisecondsToCutoff(now ?? new Date(), targetDate),
  );
  useEffect(() => {
    if (frozen !== undefined) {
      setRemaining(millisecondsToCutoff(new Date(frozen), targetDate));
      return undefined;
    }
    const tick = () => setRemaining(millisecondsToCutoff(new Date(), targetDate));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [targetDate, frozen]);
  return remaining;
}

export function CutoffCountdown({ targetDate, now }: { targetDate: string; now?: Date }) {
  const remaining = useTimeToCutoff(targetDate, now);
  return (
    <StatTile
      label="Time to 11 PM IST deadline"
      value={formatCountdown(remaining)}
      hint="At 11 PM IST every outfit not yet reviewed ships as generated."
    />
  );
}
